import { app, routing, t, useMapDispatch, useMapState, useSetInterval } from "@comact/crc";
import { Box, LoadingSpinner, styled } from "@comact/crc/modules/kit";
import _ from "lodash";
import React from "react";
import { UserModule } from "../users";
import { getActiveAccount, getMsalInstance } from "./msalAuthoConfig";

const VerticallyCentered = styled.div`
    display: flex;
    flex-flow: column;
    flex-grow: 1;
    justify-content: center;
    align-items: center;
    height: 100%;
    > ${Box} {
        width: 100%;
        text-align: center;
        > a {
            color: var(--clr-warn);
            cursor: pointer;
        }
    }
`;

const WaitForMsalLogin = React.memo<{ children: React.ReactNode; }>(({ children }) => {
    if (app.serverLess) return <>{children}</>;

    const activeAccount = getActiveAccount();

    if (app.debugMode) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const exp = activeAccount ? (activeAccount.idTokenClaims as any).exp * 1000 : 0; // check for expire date
        const timeLeftMin = activeAccount ? _.round((exp - Date.now()) / (1000 * 60), 1) : "";
        console.info("Token will expire in:", timeLeftMin + " minutes");
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [organizationsReady, hasOrganization] = useMapState((state) => {
        const myAccountOrganizations = UserModule.selectors.getMyAccountOrganizations(state);
        return [!!myAccountOrganizations, !_.isEmpty(myAccountOrganizations)];
    });

    return (
        <>
            {(() => {
                if (activeAccount && organizationsReady) {
                    if (hasOrganization) {
                        return children;
                    } else {
                        return (
                            <VerticallyCentered>
                                <Box>
                                    <p>{t("core:loginNoOrganization")}</p>
                                    <RetryLoginLink />
                                </Box>
                            </VerticallyCentered>
                        );
                    }
                } else {
                    if (window !== window.parent) {
                        return (
                            <VerticallyCentered>
                                <Box>
                                    <LoginExternal />
                                </Box>
                            </VerticallyCentered>
                        );
                    } else {
                        return <LoadingSpinner.Absolute />; // the user is not fully loaded
                    }
                }
            })()}
        </>
    );
});

export default WaitForMsalLogin;

const RetryLoginLink = React.memo(() => {
    const [isLoading, setIsLoading] = React.useState(false);
    const clearCache = useMapDispatch((dispatch) => () => dispatch(UserModule.requests.clearCache()));

    const handleRetryLogin = React.useCallback(async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (isLoading) return;

        setIsLoading(true);

        try {
            await clearCache().promise;
        } catch {
            // Best effort: continue with cleanup.
        }

        try {
            localStorage.removeItem("OPER8_ACTIVE_ACCOUNT_ID");
        } catch {
            // Best effort: continue with cleanup.
        }

        const msalInstance = getMsalInstance();
        if (msalInstance) {
            await msalInstance.logoutRedirect({ postLogoutRedirectUri: routing.projectPath });
            return;
        }

        // Fallback if MSAL is not yet initialized.
        window.location.reload();
    }, [clearCache, isLoading]);

    if (isLoading) return <LoadingSpinner.Inline />;
    return <a onClick={handleRetryLogin}>Retry login</a>;
});

const LoginExternal = () => {
    const [loginTab, setLoginTab] = React.useState<Window>(null);

    const clickHandler = React.useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        const tab = window.open(routing.projectPath + "?externalLogin=true", "_blank");
        setLoginTab(tab);
    }, []);

    useSetInterval(() => {
        if (loginTab?.closed) {
            setLoginTab(null); // remove the ref to the tab when it's closed
        }
    }, 5 * 1000, [loginTab]);

    if (loginTab) return <LoadingSpinner.Inline />;
    return <a onClick={clickHandler}>{t("core:loginReminderLink")}</a>;
};
